import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SystemActions } from 'Redux/thunks/systems.thunks';

import {
  defaultSortBy,
  defaultVisibleCols,
  defaultVisibleFacets,
  emptyFilterSystems,
  filterLabelMapping,
  getSystemsDefaultFilter,
} from '../constants/systemsListTableDefault';
import { StorageKeys } from '../constants/storageKeys';
import { getSystemsCurlCommand } from '../utils/systems.utils';

import { SystemsListContext, useSystemsListColumns } from '../hooks/useSystemsListColumns.hook';
import useLocalStorage from '../hooks/useLocalStorage';
import { useLoadInternalSystems } from '../hooks/useLoadInternalSystems.hook';

import { SystemsListPage } from './SystemsListPage.container';

import FilterTable from '../components/filter-table/FilterTable';
import { ListActions } from '../components/common/ListActions.view';

import type { RootState } from '../types/state.types';
import type { System } from '../types/system.types';

export const InternalSystemsContainer: React.FC = () => {
  const dispatch = useDispatch();

  const internalSystems = useSelector((state: RootState) => state.internalEntities.systems);
  const pipes = useSelector((state: RootState) => state.pipes);
  const inbounds = useSelector((state: RootState) => state.inbounds);
  const outbounds = useSelector((state: RootState) => state.outbounds);
  const transforms = useSelector((state: RootState) => state.transforms);
  const subUrl = useSelector((state: RootState) => state.subscription.url);
  const token = useSelector((state: RootState) => state.subscription.token);

  const columns = useSystemsListColumns(pipes, outbounds, inbounds, transforms);

  const getSelectedSystems = (selectedIds: string[]) =>
    selectedIds
      ?.map((id) => internalSystems.find((s) => s._id === id))
      ?.filter(Boolean) as System[];

  const [showBytesExact, setShowBytesExact] = useLocalStorage(
    StorageKeys.dataTable,
    ['internalSystems', 'showBytesExact'],
    true
  );

  const toggleShowBytesExact = () => {
    setShowBytesExact(!showBytesExact);
  };

  const renderActions = (selectedIds: string[], clearSelection: () => void) => {
    const selectedSystems = getSelectedSystems(selectedIds);

    const deleteBatchSystems = () => {
      dispatch(SystemActions.delete(selectedIds));
      clearSelection();
    };
    return <ListActions selection={selectedSystems} onDelete={deleteBatchSystems} name="system" />;
  };

  const { loadInternalSystems } = useLoadInternalSystems(subUrl, token);

  const handleFilterChanged = useCallback(() => {
    loadInternalSystems();
  }, [loadInternalSystems]);

  return (
    <SystemsListContext.Provider value={{ showBytesExact, toggleShowBytesExact }}>
      <SystemsListPage includeInternal={true} shouldRenderRefreshButton="render">
        <FilterTable
          actionsRenderer={renderActions}
          defaults={{
            filter: getSystemsDefaultFilter('system'),
            sortBy: defaultSortBy,
            visibleCols: defaultVisibleCols,
            visibleFacets: defaultVisibleFacets,
          }}
          emptyFilter={emptyFilterSystems}
          filterLabelMapping={filterLabelMapping}
          columns={columns}
          itemsToFilter={internalSystems}
          tableKey="internalSystems"
          onFilterChanged={handleFilterChanged}
          curl={getSystemsCurlCommand(subUrl, token, false)}
        />
      </SystemsListPage>
    </SystemsListContext.Provider>
  );
};
